<template>
	<transition name="fade">
		<div
			v-if="isOpen"
			class="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center px-0 sm:px-4"
			@click.self="closeModal">
			<!-- Backdrop -->
			<div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

			<!-- Sheet / Modal -->
			<div
				class="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl border shadow-2xl z-10 overflow-hidden"
				:class="
					isLight
						? 'bg-white border-[#1a9b8a]/20'
						: 'bg-[#132420] border-[#1a9b8a]/20'
				">
				<!-- Imigongo accent bar -->
				<div
					class="h-1 w-full bg-gradient-to-r from-[#0d6b5e] via-[#1a9b8a] to-[#a8d5cc]"></div>

				<!-- Drag handle (mobile) -->
				<div class="flex justify-center pt-3 pb-1 sm:hidden">
					<div
						class="w-10 h-1 rounded-full"
						:class="isLight ? 'bg-black/15' : 'bg-white/20'"></div>
				</div>

				<div class="px-5 sm:px-6 pt-4 pb-6 sm:pt-6">
					<!-- Header -->
					<div class="flex items-start justify-between mb-5">
						<div class="flex items-center gap-3">
							<!-- Music icon badge -->
							<div
								class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
								:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/10'">
								<svg
									class="w-5 h-5"
									:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.75"
										d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
								</svg>
							</div>
							<div class="items-center">
								<p
									class="font-['Montserrat'] font-extrabold text-base leading-tight tracking-tight"
									:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{ $t("music.title") }}
								</p>
								<p
									class="text-[11px] font-semibold tracking-widest uppercase mt-0.5"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									{{ $t("music.subtitle") }}
								</p>
							</div>
						</div>

						<!-- Close button -->
						<button
							@click="closeModal"
							class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200 flex-shrink-0"
							:class="
								isLight
									? 'border-black/10 text-[#0f1f1c]/40 hover:text-[#0f1f1c] hover:border-black/20'
									: 'border-white/10 text-white/30 hover:text-white hover:border-white/20'
							">
							<svg
								class="w-3.5 h-3.5"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					<!-- Status message -->
					<transition name="slide-down">
						<div
							v-if="message.text"
							class="mb-4 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-medium border"
							:class="{
								'bg-emerald-500/10 border-emerald-500/25 text-emerald-600 dark:text-emerald-400':
									message.type === 'success',
								'bg-red-500/10 border-red-500/25 text-red-600 dark:text-red-400':
									message.type === 'error',
								'bg-amber-400/10 border-amber-400/25 text-amber-700 dark:text-amber-400':
									message.type === 'warning',
								'bg-[#1a9b8a]/10 border-[#1a9b8a]/25': message.type === 'info',
							}">
							<span>{{ message.text }}</span>
						</div>
					</transition>

					<!-- Email field -->
					<div v-if="!emailVerified" class="mb-4">
						<label
							class="text-[11px] font-semibold tracking-widest uppercase block mb-1.5"
							:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
							{{ $t("music.emailLabel") }}
						</label>
						<div class="relative">
							<div
								class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
								<svg
									class="w-3.5 h-3.5"
									:class="isLight ? 'text-[#0f1f1c]/30' : 'text-white/25'"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
							</div>
							<input
								v-model="localEmail"
								@blur="checkEmailExists"
								type="email"
								:placeholder="$t('music.emailPlaceholder')"
								class="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border outline-none transition-all duration-200"
								:class="
									isLight
										? 'bg-black/[0.03] border-black/10 text-[#0f1f1c] placeholder-[#0f1f1c]/30 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'
										: 'bg-white/[0.05] border-white/[0.08] text-white placeholder-white/25 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'
								" />
							<!-- Loading spinner -->
							<div
								v-if="isEmailLoading"
								class="absolute right-3 top-1/2 -translate-y-1/2">
								<div
									class="w-3.5 h-3.5 rounded-full border border-t-transparent animate-spin"
									:class="
										isLight ? 'border-[#1a9b8a]' : 'border-[#a8d5cc]'
									"></div>
							</div>
						</div>
					</div>

					<!-- Verified email chip -->
					<div
						v-else
						class="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold"
						:class="
							isLight
								? 'bg-emerald-500/8 border-emerald-500/20 text-emerald-700'
								: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
						">
						<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd" />
						</svg>
						{{ localEmail }}
					</div>

					<!-- Divider -->
					<div
						class="border-t mb-4"
						:class="isLight ? 'border-black/8' : 'border-white/[0.06]'"></div>

					<!-- Song search -->
					<div class="mb-3">
						<label
							class="text-[11px] font-semibold tracking-widest uppercase block mb-1.5"
							:class="
								isLight
									? emailVerified
										? 'text-[#0d6b5e]'
										: 'text-[#0f1f1c]/25'
									: emailVerified
									? 'text-[#a8d5cc]'
									: 'text-white/20'
							">
							{{ $t("music.searchLabel") }}
						</label>
						<div class="relative">
							<div
								class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
								<svg
									class="w-3.5 h-3.5"
									:class="isLight ? 'text-[#0f1f1c]/30' : 'text-white/25'"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
								</svg>
							</div>
							<input
								v-model="songQuery"
								@input="debouncedSearch"
								type="text"
								:placeholder="$t('music.searchPlaceholder')"
								:disabled="!emailVerified"
								class="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border outline-none transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
								:class="
									isLight
										? 'bg-black/[0.03] border-black/10 text-[#0f1f1c] placeholder-[#0f1f1c]/30 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'
										: 'bg-white/[0.05] border-white/[0.08] text-white placeholder-white/25 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'
								" />
						</div>
					</div>

					<!-- Search results -->
					<transition name="fade">
						<ul
							v-if="searchResults.length"
							class="rounded-xl border overflow-hidden mb-4 max-h-52 overflow-y-auto scrollbar-none"
							:class="isLight ? 'border-black/8' : 'border-white/[0.06]'">
							<li
								v-for="track in searchResults"
								:key="track.id"
								class="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-150"
								:class="
									isLight
										? 'hover:bg-[#1a9b8a]/8 border-b border-black/5 last:border-0'
										: 'hover:bg-[#1a9b8a]/10 border-b border-white/[0.04] last:border-0'
								"
								@click="selectSong(track)">
								<img
									:src="track.album.images[0]?.url"
									alt="album"
									class="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
								<div class="min-w-0 flex-1">
									<p
										class="text-sm font-semibold truncate"
										:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
										{{ track.name }}
									</p>
									<p
										class="text-xs truncate mt-0.5"
										:class="isLight ? 'text-[#0f1f1c]/45' : 'text-white/40'">
										{{ track.artists.map((a) => a.name).join(", ") }}
									</p>
								</div>
								<svg
									class="w-3.5 h-3.5 flex-shrink-0"
									:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 4v16m8-8H4" />
								</svg>
							</li>
						</ul>
					</transition>

					<!-- No results -->
					<p
						v-if="songQuery && !searchResults.length && !selectedSong"
						class="text-xs text-center mb-4"
						:class="isLight ? 'text-[#0f1f1c]/35' : 'text-white/30'">
						{{ $t("music.noResults", { query: songQuery }) }}
					</p>

					<!-- Selected song preview -->
					<transition name="slide-down">
						<div
							v-if="selectedSong && !searchResults.length"
							class="mb-4 flex items-center gap-3 px-3 py-2.5 rounded-xl border"
							:class="
								isLight
									? 'bg-[#1a9b8a]/8 border-[#1a9b8a]/20'
									: 'bg-[#1a9b8a]/10 border-[#1a9b8a]/20'
							">
							<img
								:src="selectedSong.album.images[0]?.url"
								alt="album"
								class="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
							<div class="min-w-0 flex-1">
								<p
									class="text-sm font-semibold truncate"
									:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{ selectedSong.name }}
								</p>
								<p
									class="text-xs truncate mt-0.5"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									{{ selectedSong.artists.map((a) => a.name).join(", ") }}
								</p>
							</div>
							<!-- Clear selection -->
							<button
								@click="clearSelection"
								class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150"
								:class="
									isLight
										? 'text-[#0f1f1c]/30 hover:text-[#0f1f1c]/60 hover:bg-black/8'
										: 'text-white/25 hover:text-white/50 hover:bg-white/8'
								">
								<svg
									class="w-3 h-3"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
					</transition>

					<!-- Submit -->
					<button
						@click="submitRecommendation"
						class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20 disabled:opacity-40 disabled:cursor-not-allowed"
						:disabled="!selectedSong || !emailVerified">
						{{ $t("music.submit") }}
					</button>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import apiService from "@/services/api";
import { debounce } from "lodash";
import { useToast } from "vue-toastification";
import { useTheme } from "@/composables/useTheme";

export default {
	props: {
		isOpen: { type: Boolean, required: true },
		hideEmail: { type: Boolean, default: false },
		event: { type: Object, default: null },
		email: { type: String, default: "" },
	},

	setup() {
		const toast = useToast();
		const { isLight } = useTheme();
		return { toast, isLight };
	},

	data() {
		return {
			localEmail: this.email,
			isEmailLoading: false,
			emailVerified: this.hideEmail,
			songQuery: "",
			searchResults: [],
			selectedSong: null,
			message: { text: "", type: "" },
		};
	},

	created() {
		this.debouncedSearch = debounce(this.searchMusic, 300);
	},

	methods: {
		closeModal() {
			this.$emit("close");
		},

		async checkEmailExists() {
			if (!this.localEmail) return;
			this.isEmailLoading = true;
			this.message = { text: "", type: "" };
			try {
				const response = await apiService.checkTicketExist(
					this.localEmail,
					this.event.event_id,
				);
				if (response.code === 302) {
					this.emailVerified = true;
					this.message = {
						text: "Ticket found. Search for your song below.",
						type: "success",
					};
				}
			} catch {
				this.emailVerified = false;
				this.message = {
					text: " No ticket found for this email. Please book first.",
					type: "error",
				};
			} finally {
				this.isEmailLoading = false;
			}
		},

		async searchMusic() {
			if (!this.songQuery) {
				this.searchResults = [];
				return;
			}
			try {
				const response = await apiService.musicRecommandation(this.songQuery);
				this.searchResults = response.data[0]?.tracks?.items || [];
			} catch {
				this.searchResults = [];
			}
		},

		selectSong(track) {
			this.selectedSong = track;
			this.songQuery = `${track.name} – ${track.artists
				.map((a) => a.name)
				.join(", ")}`;
			this.searchResults = [];
		},

		clearSelection() {
			this.selectedSong = null;
			this.songQuery = "";
		},

		async submitRecommendation() {
			if (!this.selectedSong) {
				this.toast.warning("Please select a song first!");
				return;
			}
			try {
				await apiService.musicSuggestion(
					this.event.event_id,
					this.localEmail,
					this.selectedSong.id,
				);
				this.toast.success("Song recommendation submitted!");
				this.closeModal();
			} catch (error) {
				this.toast.error(error?.response?.data?.error || "Submission failed.");
				this.closeModal();
			}
		},
	},
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}

.scrollbar-none::-webkit-scrollbar {
	display: none;
}
.scrollbar-none {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
</style>
